import React, { useState, useEffect } from 'react';
import { Sentry } from '@/lib/sentry';
import { createRoot } from 'react-dom/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Loader2, Eye } from 'lucide-react';
import { useSendDocument } from '@/hooks/useSendDocument';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { fetchEstimateExportData } from '@/utils/estimateExportUtils';
import { format } from 'date-fns';
import { fetchChangeOrderExportData } from '@/utils/changeOrderExportUtils';
import EstimatePdfStylePreview from './preview/EstimatePdfStylePreview';
import EstimatePreviewModal from './EstimatePreviewModal';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';
import { gridToHtml, jsonToGridData } from '@/components/settings/signature-builder/utils/signatureToHtml';
import RichTextEditorWithDynamicFields from '@/components/cost-center/document-defaults/RichTextEditorWithDynamicFields';
import { convertToEmailHtml, ensureHtml } from '@/utils/emailHtmlConverter';
import { formatCurrency } from '@/lib/utils';

interface SendEstimateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimateId: string;
  estimateNumber?: string;
  customerId?: string;
  companyId: string;
  siteAddress?: string;
  /** Optional version ID - when provided, sends this specific version */
  versionId?: string;
}

const SendEstimateDialog: React.FC<SendEstimateDialogProps> = ({
  open,
  onOpenChange,
  estimateId,
  estimateNumber,
  customerId,
  companyId,
  siteAddress,
  versionId,
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const queryClient = useQueryClient();
  const { sendDocument, isSending } = useSendDocument();
  
  // Fetch version details for confirmation display
  const { data: versionData } = useQuery({
    queryKey: ['estimate-version-for-send', versionId],
    queryFn: async () => {
      if (!versionId) return null;
      const { data, error } = await supabase
        .from('estimate_versions')
        .select('version_number, version_type, status, total_amount')
        .eq('id', versionId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!versionId && open,
  });

  // Fetch current user profile for dynamic fields
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile-for-email'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, personal_phone_number, email_signature, company_position')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Fetch company data for dynamic fields
  const { data: company } = useQuery({
    queryKey: ['company-for-email', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('name, email, company_phone_number, street_address, suburb, city, province_state, postal_code, website, "Bank Name", "Branch Name", "Branch Code", "Account Number", "Account Holder"')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  // Fetch customer data
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async () => {
      if (!customerId) return null;
      const { data, error } = await supabase
        .from('customers')
        .select('name, last_name, emails, business_email, customer_type, cell_numbers, street_address, suburb, city, province_state, postal_code')
        .eq('id', customerId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!customerId && open,
  });

  // Fetch estimate data for dynamic fields (including use_separate_site_address for fallback logic)
  const { data: estimate } = useQuery({
    queryKey: ['estimate-for-email', estimateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimates')
        .select('estimate_number, created_at, estimate_date, expires_at, validity_period, use_separate_site_address, site_street_address, site_suburb, site_city, site_province_state, site_postal_code')
        .eq('id', estimateId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!estimateId && open,
  });

  // Fetch document defaults for email template and signature template
  const { data: documentDefaults } = useQuery({
    queryKey: ['document-defaults', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_defaults')
        .select('estimate_email_template, estimate_email_subject, email_signature_template')
        .eq('company_id', companyId)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  // Pre-fill form when data loads
  useEffect(() => {
    if (customer) {
      const fullName = `${customer.name} ${customer.last_name || ''}`.trim();
      setRecipientName(fullName);
      
      // Get primary email
      const email = customer.customer_type === 'commercial' 
        ? customer.business_email 
        : customer.emails?.[0];
      if (email) setRecipientEmail(email);
    }
  }, [customer]);

  useEffect(() => {
    if (documentDefaults?.estimate_email_subject && customer) {
      const customerFullName = `${customer.name} ${customer.last_name || ''}`.trim();
      const customerEmail = customer.customer_type === 'commercial' 
        ? customer.business_email 
        : customer.emails?.[0];
      const today = format(new Date(), 'dd MMMM yyyy');
      
      // Build site address - use estimate's site address only if enabled AND has data, otherwise fall back to customer address
      const useSiteAddress = estimate?.use_separate_site_address && estimate?.site_street_address;
      const siteAddressFields = useSiteAddress ? {
        street_address: estimate.site_street_address,
        suburb: estimate.site_suburb,
        city: estimate.site_city,
        province_state: estimate.site_province_state,
        postal_code: estimate.site_postal_code,
      } : {
        street_address: customer.street_address,
        suburb: customer.suburb,
        city: customer.city,
        province_state: customer.province_state,
        postal_code: customer.postal_code,
      };
      
      // Process subject with all dynamic fields
      const processedSubject = processDynamicFields(documentDefaults.estimate_email_subject, {
        user: {
          first_name: userProfile?.first_name || undefined,
          last_name: userProfile?.last_name || undefined,
          email: userProfile?.email || undefined,
          personal_phone_number: userProfile?.personal_phone_number || undefined,
        },
        company: {
          name: company?.name || undefined,
          email: company?.email || undefined,
          company_phone_number: company?.company_phone_number || undefined,
        },
        customer: {
          name: customerFullName,
          first_name: customer.name || undefined,
          last_name: customer.last_name || undefined,
          email: customerEmail || undefined,
        },
        estimate: {
          estimate_number: estimate?.estimate_number || estimateNumber || undefined,
          created_date: estimate ? format(new Date((estimate as any).estimate_date || estimate.created_at), 'dd MMMM yyyy') : undefined,
          expires_date: estimate?.expires_at ? format(new Date(estimate.expires_at), 'dd MMMM yyyy') : undefined,
          validity_days: estimate?.validity_period || undefined,
          site_street_address: siteAddressFields.street_address || undefined,
          site_suburb: siteAddressFields.suburb || undefined,
          site_city: siteAddressFields.city || undefined,
          site_province_state: siteAddressFields.province_state || undefined,
          site_postal_code: siteAddressFields.postal_code || undefined,
        },
        project: {
          site_street_address: siteAddressFields.street_address || undefined,
          site_suburb: siteAddressFields.suburb || undefined,
          site_city: siteAddressFields.city || undefined,
          site_province_state: siteAddressFields.province_state || undefined,
          site_postal_code: siteAddressFields.postal_code || undefined,
        },
        system: {
          today_date: today,
        },
      });
      setSubject(processedSubject);
    } else if (estimateNumber) {
      setSubject(`Estimate ${estimateNumber}`);
    }
  }, [documentDefaults, estimateNumber, customer, userProfile, company, estimate, siteAddress]);

  useEffect(() => {
    if (documentDefaults?.estimate_email_template && customer) {
      const customerFullName = `${customer.name} ${customer.last_name || ''}`.trim();
      const customerEmail = customer.customer_type === 'commercial' 
        ? customer.business_email 
        : customer.emails?.[0];
      const today = format(new Date(), 'dd MMMM yyyy');
      
      // Build site address - use estimate's site address only if enabled AND has data, otherwise fall back to customer address
      const useSiteAddress = estimate?.use_separate_site_address && estimate?.site_street_address;
      const siteAddressFields = useSiteAddress ? {
        street_address: estimate.site_street_address,
        suburb: estimate.site_suburb,
        city: estimate.site_city,
        province_state: estimate.site_province_state,
        postal_code: estimate.site_postal_code,
      } : {
        street_address: customer.street_address,
        suburb: customer.suburb,
        city: customer.city,
        province_state: customer.province_state,
        postal_code: customer.postal_code,
      };
      
      // Process template with all dynamic fields
      const processedTemplate = processDynamicFields(documentDefaults.estimate_email_template, {
        user: {
          first_name: userProfile?.first_name || undefined,
          last_name: userProfile?.last_name || undefined,
          email: userProfile?.email || undefined,
          personal_phone_number: userProfile?.personal_phone_number || undefined,
        },
        company: {
          name: company?.name || undefined,
          email: company?.email || undefined,
          company_phone_number: company?.company_phone_number || undefined,
          street_address: company?.street_address || undefined,
          suburb: company?.suburb || undefined,
          city: company?.city || undefined,
          province_state: company?.province_state || undefined,
          postal_code: company?.postal_code || undefined,
          website: company?.website || undefined,
          bank_name: company?.['Bank Name'] || undefined,
          branch_name: company?.['Branch Name'] || undefined,
          branch_code: company?.['Branch Code'] || undefined,
          account_number: company?.['Account Number'] || undefined,
          account_holder: company?.['Account Holder'] || undefined,
        },
        customer: {
          name: customerFullName,
          first_name: customer.name || undefined,
          last_name: customer.last_name || undefined,
          email: customerEmail || undefined,
          cell_numbers: customer.cell_numbers || undefined,
          street_address: customer.street_address || undefined,
          suburb: customer.suburb || undefined,
          city: customer.city || undefined,
          province_state: customer.province_state || undefined,
          postal_code: customer.postal_code || undefined,
        },
        estimate: {
          estimate_number: estimate?.estimate_number || estimateNumber || undefined,
          created_date: estimate ? format(new Date((estimate as any).estimate_date || estimate.created_at), 'dd MMMM yyyy') : undefined,
          expires_date: estimate?.expires_at ? format(new Date(estimate.expires_at), 'dd MMMM yyyy') : undefined,
          validity_days: estimate?.validity_period || undefined,
          site_street_address: siteAddressFields.street_address || undefined,
          site_suburb: siteAddressFields.suburb || undefined,
          site_city: siteAddressFields.city || undefined,
          site_province_state: siteAddressFields.province_state || undefined,
          site_postal_code: siteAddressFields.postal_code || undefined,
        },
        project: {
          site_street_address: siteAddressFields.street_address || undefined,
          site_suburb: siteAddressFields.suburb || undefined,
          site_city: siteAddressFields.city || undefined,
          site_province_state: siteAddressFields.province_state || undefined,
          site_postal_code: siteAddressFields.postal_code || undefined,
        },
        system: {
          today_date: today,
        },
      });
      
      setMessage(processedTemplate);
    } else if (customer) {
      // Default message
      setMessage(`Hi ${customer.name},\n\nPlease find your estimate attached.\n\nBest regards`);
    }
  }, [documentDefaults, customer, userProfile, company, estimate, siteAddress, estimateNumber]);

  // Helper to render EstimatePdfStylePreview and capture its HTML
  const renderPreviewToHtml = async (exportData: any): Promise<string> => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    try {
      const root = createRoot(container);
      await new Promise<void>((resolve) => {
        root.render(<EstimatePdfStylePreview data={exportData} />);
        setTimeout(resolve, 150); // Give React time to render
      });

      const previewElement = container.querySelector('.pdf-page-container');
      if (!previewElement) {
        throw new Error('Preview element not found');
      }

      return previewElement.outerHTML;
    } finally {
      document.body.removeChild(container);
    }
  };

  // Helper to update version status to sent
  const updateVersionStatusToSent = async (estId: string, targetVersionId?: string): Promise<string | null> => {
    // If a specific versionId is provided, update that version
    if (targetVersionId) {
      const { data: version, error: fetchError } = await supabase
        .from('estimate_versions')
        .select('status')
        .eq('id', targetVersionId)
        .single();
      
      if (fetchError) {
        console.error('[SendEstimateDialog] Error fetching version status:', fetchError);
        throw fetchError;
      }
      
      if (version?.status !== 'draft') {
        return null; // Not a draft, no change needed
      }
      
      const { error: updateError } = await supabase
        .from('estimate_versions')
        .update({ status: 'sent', sent_date: new Date().toISOString() })
        .eq('id', targetVersionId);
      
      if (updateError) {
        console.error('[SendEstimateDialog] Error updating version status:', updateError);
        throw updateError;
      }
      
      console.log('[SendEstimateDialog] Updated specific version status from draft to sent');
      
      // Lock expires_at at sent_date + validity_period
      const { data: estData } = await supabase
        .from('estimates')
        .select('validity_period')
        .eq('id', estId)
        .single();
      let validityPeriod = estData?.validity_period;
      if (validityPeriod == null) {
        const { data: defaults } = await supabase
          .from('document_defaults')
          .select('estimate_validity_days')
          .eq('company_id', companyId)
          .maybeSingle();
        validityPeriod = defaults?.estimate_validity_days ?? 14;
      }
      await supabase
        .from('estimates')
        .update({
          expires_at: new Date(Date.now() + (validityPeriod * 24 * 60 * 60 * 1000)).toISOString()
        })
        .eq('id', estId);
      
      return targetVersionId;
    }
    
    // Otherwise, use the main version (existing behavior)
    const { data: est, error: fetchError } = await supabase
      .from('estimates')
      .select('status, main_version_id, validity_period')
      .eq('id', estId)
      .single();
    
    if (fetchError) {
      console.error('[SendEstimateDialog] Error fetching estimate status:', fetchError);
      throw fetchError;
    }
    
    if (est?.status !== 'draft' || !est.main_version_id) {
      return null; // Not a draft or no main version, no change needed
    }
    
    // Update the main version's status to 'sent'
    const { error: updateError } = await supabase
      .from('estimate_versions')
      .update({ status: 'sent', sent_date: new Date().toISOString() })
      .eq('id', est.main_version_id);
    
    if (updateError) {
      console.error('[SendEstimateDialog] Error updating estimate status:', updateError);
      throw updateError;
    }
    
    // Lock expires_at at sent_date + validity_period
    let validityPeriod2 = est.validity_period;
    if (validityPeriod2 == null) {
      const { data: defaults } = await supabase
        .from('document_defaults')
        .select('estimate_validity_days')
        .eq('company_id', companyId)
        .maybeSingle();
      validityPeriod2 = defaults?.estimate_validity_days ?? 14;
    }
    await supabase
      .from('estimates')
      .update({
        expires_at: new Date(Date.now() + (validityPeriod2 * 24 * 60 * 60 * 1000)).toISOString()
      })
      .eq('id', estId);
    
    console.log('[SendEstimateDialog] Updated estimate status from draft to sent');
    return est.main_version_id;
  };

  // Helper to revert estimate status back to draft on failure
  const revertEstimateStatusToDraft = async (versionId: string): Promise<void> => {
    const { error } = await supabase
      .from('estimate_versions')
      .update({ status: 'draft', sent_date: null })
      .eq('id', versionId);
    
    if (error) {
      console.error('[SendEstimateDialog] Error reverting estimate status:', error);
    } else {
      console.log('[SendEstimateDialog] Reverted estimate status to draft');
    }
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }

    if (!recipientEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsGeneratingPdf(true);
    let updatedVersionId: string | null = null;

    try {
      // Step 1: Update status from 'draft' to 'sent' BEFORE generating PDF
      updatedVersionId = await updateVersionStatusToSent(estimateId, versionId);

      // Step 2: Fetch complete estimate data for PDF generation (now reflects 'sent' status)
      // Check if this is a change order and use appropriate fetch function
      console.log('[SendEstimateDialog] Fetching estimate data for PDF...', { versionId });
      
      let exportData;
      if (versionId) {
        // Check version type to determine correct fetch function
        const { data: version } = await supabase
          .from('estimate_versions')
          .select('version_type')
          .eq('id', versionId)
          .single();
          
        if (version?.version_type === 'change_order') {
          console.log('[SendEstimateDialog] Detected change order - using fetchChangeOrderExportData');
          exportData = await fetchChangeOrderExportData(estimateId, versionId);
        } else {
          console.log('[SendEstimateDialog] Regular version - using fetchEstimateExportData');
          exportData = await fetchEstimateExportData(estimateId, versionId);
        }
      } else {
        console.log('[SendEstimateDialog] No version ID - using fetchEstimateExportData');
        exportData = await fetchEstimateExportData(estimateId);
      }

      // Render the preview component and capture its HTML
      console.log('[SendEstimateDialog] Rendering preview component...');
      const previewHtml = await renderPreviewToHtml(exportData);

      // Call edge function to enhance HTML with proper styles (same as preview modal)
      console.log('[SendEstimateDialog] Enhancing HTML via edge function...');
      const { data: enhancedResponse, error: enhanceError } = await supabase.functions.invoke('generate-estimate-pdf', {
        body: {
          htmlContent: previewHtml,
          estimateData: exportData
        }
      });

      if (enhanceError) throw new Error(enhanceError.message);

      // Generate PDF using Puppeteer edge function (respects @page CSS rules)
      console.log('[SendEstimateDialog] Generating PDF via Puppeteer...');
      const { data: pdfResponse, error: pdfError } = await supabase.functions.invoke('generate-pdf-puppeteer', {
        body: {
          htmlContent: enhancedResponse.htmlContent
        }
      });

      if (pdfError) throw new Error(pdfError.message);
      if (!pdfResponse?.pdfBase64) throw new Error('No PDF data returned');
      
      const pdfBase64 = pdfResponse.pdfBase64;

      const pdfFilename = `Estimate_${estimateNumber || estimateId}.pdf`;

      // Process email signature - prefer company template, fall back to user's personal signature
      let processedSignature = '';
      if (documentDefaults?.email_signature_template) {
        // Use company template with user's data
        const gridData = jsonToGridData(documentDefaults.email_signature_template);
        if (gridData) {
          // Use absolute URLs for email so icons work in all email clients
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          processedSignature = gridToHtml(gridData, {
            user: {
              first_name: userProfile?.first_name,
              last_name: userProfile?.last_name,
              email: userProfile?.email,
              personal_phone_number: userProfile?.personal_phone_number,
              company_position: userProfile?.company_position,
            },
            company: {
              name: company?.name,
              email: company?.email,
              company_phone_number: company?.company_phone_number,
              website: company?.website,
            },
          }, { baseUrl });
        }
      } else if (userProfile?.email_signature) {
        // Fall back to user's stored signature
        processedSignature = processDynamicFields(userProfile.email_signature, {
          user: {
            first_name: userProfile.first_name || undefined,
            last_name: userProfile.last_name || undefined,
            email: userProfile.email || undefined,
            personal_phone_number: userProfile.personal_phone_number || undefined,
            company_position: userProfile.company_position || undefined,
          },
          company: {
            name: company?.name || undefined,
            email: company?.email || undefined,
            company_phone_number: company?.company_phone_number || undefined,
          },
        });
      }

      // Convert message to email-safe HTML
      const messageHtml = ensureHtml(message);
      const emailSafeMessage = convertToEmailHtml(messageHtml, { baseUrl: typeof window !== 'undefined' ? window.location.origin : '' });
      
      // Build final email body with signature
      const bodyHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0;">
          ${emailSafeMessage}
          ${processedSignature ? `
            <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e5e5;">
              ${processedSignature}
            </div>
          ` : ''}
        </div>
      `;

      await sendDocument({
        documentType: 'estimate',
        documentId: estimateId,
        versionId: versionId || undefined,
        recipientEmail,
        recipientName,
        subject,
        bodyHtml,
        pdfBase64,
        pdfFilename,
        companyId,
      });

      // Invalidate queries to refresh UI with new status (use correct camelCase query keys)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] }),
        queryClient.invalidateQueries({ queryKey: ['estimateVersions', estimateId] }),
        queryClient.invalidateQueries({ queryKey: ['estimates'] }),
        queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] }),
      ]);
      
      // Force immediate refetch for instant UI update
      await queryClient.refetchQueries({ queryKey: ['estimateVersions', estimateId] });

      onOpenChange(false);
    } catch (error: any) {
      // Rollback status if we updated it
      if (updatedVersionId) {
        await revertEstimateStatusToDraft(updatedVersionId);
      }

      console.error('[SendEstimateDialog] Error sending estimate:', error);
      toast.error(`Failed to send estimate: ${error.message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const isLoading = isSending || isGeneratingPdf;
  const isDataLoading = !userProfile || !company || !customer || !documentDefaults;

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-destructive">Something went wrong loading the send dialog.</div>}>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Estimate
          </DialogTitle>
          <DialogDescription>
            Send this estimate to your customer via email with PDF attachment.
          </DialogDescription>
        </DialogHeader>

        {isDataLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading template...</span>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Version Confirmation Section */}
            {versionData && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      You're about to send:
                    </p>
                    <p className="text-lg font-semibold">
                      {versionData.version_type === 'change_order' 
                        ? `Change Order v${versionData.version_number}` 
                        : `Estimate v${versionData.version_number}`}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground">
                        Total: {formatCurrency(versionData.total_amount || 0)}
                      </span>
                      {versionData.status === 'draft' && (
                        <Badge variant="outline" className="text-xs">Draft</Badge>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowPreview(true)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="customer@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient-name">Recipient Name</Label>
              <Input
                id="recipient-name"
                placeholder="John Doe"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Your estimate"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <RichTextEditorWithDynamicFields
                value={message}
                onChange={setMessage}
                placeholder="Enter your message..."
                minHeight="180px"
              />
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && (
          <EstimatePreviewModal
            estimateId={estimateId}
            versionId={versionId}
            isOpen={showPreview}
            onClose={() => setShowPreview(false)}
          />
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isLoading} className="w-full sm:w-auto">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isGeneratingPdf ? 'Generating PDF...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default SendEstimateDialog;
